import {
  Alert,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  getConfigSections,
  deriveDefaultConfigFromMetadata
} from "~/config/clientConfigFieldMetadata";
import {
  useGetClientConfigQuery,
  useUpdateClientConfigMutation
} from "~/redux/warehouse/clientConfig.hooks";

import { ConfigSectionPanel } from "./ConfigSectionPanel";

const UpdateConfiguration = () => {
  const { t } = useTranslation();
  const { successToast, errorToast, infoToast } = useToast();

  // Step 1: GET current config with ETag
  const { data: apiConfig, isLoading, error } = useGetClientConfigQuery();

  const [updateConfig] = useUpdateClientConfigMutation();

  const [configJson, setConfigJson] = useState<string>("");
  const [configObject, setConfigObject] = useState<Record<string, unknown>>({});
  const [originalConfigJson, setOriginalConfigJson] = useState<string>("");
  const [jsonError, setJsonError] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<"form" | "json">("form");

  useEffect(() => {
    if (apiConfig?.clientConfiguration) {
      try {
        const configString = apiConfig.clientConfiguration;
        const parsed = JSON.parse(configString) as unknown;
        const formattedJson = JSON.stringify(parsed, null, 2);
        setConfigJson(formattedJson);
        setOriginalConfigJson(formattedJson);
        setConfigObject(parsed as Record<string, unknown>);
      } catch {
        // Silently handle parse errors
      }
    }
  }, [apiConfig]);

  const handleJsonChange = (value: string) => {
    setConfigJson(value);
    setJsonError("");

    // Validate JSON and sync with config object
    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === "object") {
        setConfigObject(parsed as Record<string, unknown>);
      }
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const handleFieldChange = (key: string, value: unknown) => {
    const updated = {
      ...configObject,
      [key]: value
    };
    setConfigObject(updated);
    setConfigJson(JSON.stringify(updated, null, 2));
    setJsonError("");
  };

  const handleResetToDefaults = () => {
    const defaults = deriveDefaultConfigFromMetadata();
    setConfigObject(defaults as Record<string, unknown>);
    setConfigJson(JSON.stringify(defaults, null, 2));
    infoToast(t("reset to generic default values"));
  };

  const handleSaveConfig = () => {
    if (!apiConfig?.etag) {
      errorToast(t("no etag available"));
      return;
    }

    const payload = {
      ClientConfiguration: JSON.stringify(configObject),
      etag: apiConfig.etag
    };

    updateConfig(payload)
      .unwrap()
      .then(() => {
        successToast(t("configuration updated successfully"));
        // Reset original config to current config after successful save
        setOriginalConfigJson(configJson);
      })
      .catch((error: unknown) => {
        // Handle 409 conflict (ETag mismatch) with friendly message
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 409
        ) {
          errorToast(t("config update conflict"), {
            description: t("config update conflict message")
          });
        } else {
          // Other errors
          errorToast(t("update failed"), {
            description: JSON.stringify(error, null, 2)
          });
        }
      });
  };

  if (isLoading) {
    return <Typography>{t("loading")}...</Typography>;
  }

  if (error) {
    return (
      <Alert severity="error">
        {t("error loading")}: {JSON.stringify(error)}
      </Alert>
    );
  }

  const sections = getConfigSections();
  const hasChanges = configJson !== originalConfigJson;

  return (
    <Stack spacing={2} sx={{ p: 2, maxWidth: 1200 }}>
      <Typography variant="h4">{t("client configuration")}</Typography>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>{t("etag")}:</strong> {apiConfig?.etag || t("not found")}
        </Typography>
      </Alert>

      {/* Action Buttons at Top */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <Button onClick={handleResetToDefaults}>
          {t("reset to generic defaults")}
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={handleSaveConfig}
          disabled={!!jsonError || !apiConfig?.etag || !hasChanges}
          size="large"
          color={hasChanges ? "primary" : "inherit"}
        >
          {t("save configuration")}
        </Button>
      </Box>

      {jsonError && <Alert severity="error">{jsonError}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={currentTab}
          onChange={(_, val) => setCurrentTab(val as "form" | "json")}
        >
          <Tab label={t("form view")} value="form" />
          <Tab label={t("json editor (advanced)")} value="json" />
        </Tabs>
      </Box>

      {currentTab === "form" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("use the form below to configure settings")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr"
              },
              gap: { xs: 2, md: 4 },
              alignItems: "start"
            }}
          >
            {Array.from(sections.entries()).map(([sectionName, fields]) => (
              <ConfigSectionPanel
                key={sectionName}
                sectionName={sectionName}
                fields={fields}
                config={configObject}
                onChange={handleFieldChange}
              />
            ))}
          </Box>
        </Box>
      )}

      {currentTab === "json" && (
        <TextField
          label={t("client configuration")}
          multiline
          rows={30}
          value={configJson}
          onChange={(e) => handleJsonChange(e.target.value)}
          error={!!jsonError}
          helperText={jsonError || t("edit json directly (advanced users)")}
          fullWidth
          slotProps={{
            input: {
              style: { fontFamily: "monospace", fontSize: 12 }
            }
          }}
        />
      )}
    </Stack>
  );
};

export default UpdateConfiguration;
